/* ONE BOUT — ob-cart.js (Sprint 3 / K1), loaded by sections/cart-drawer.liquid as a module.
   <ob-cart-upsell> "Doporučujeme přidat": the drawer server-renders the manual + fallback-collection cards; this
   element asks the Product Recommendations endpoint to render the SAME section (section_id) for the newest cart line
   → only the cards come back (cart-drawer.liquid, recommendations.performed?). Order: manual → complementary →
   fallback collection → related (only when complementary has fewer than data-min). Dropped: products in the cart,
   duplicates and same-title products (colours are separate products: no "same tee in black" next to the white one).
   Adding to cart = theme.js `.one-bout-quick-buy` handler (cart/add.js + cart:change → Prestige re-renders the drawer,
   which re-creates this element with the new cart). Multi-variant cards: "ZVOLIT VELIKOST" toggles the size panel.
   The free-shipping bar needs no JS (snippets/free-shipping-bar.liquid is server-rendered on every re-render). */
const cache = new Map();

function fetchCards(url, key) {
  if (!cache.has(url + key)) {
    cache.set(url + key, fetch(url)
      .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return [...doc.querySelectorAll('[data-ob-upsell-results] .ob-upsell__card')];
      })
      .catch(() => {
        cache.delete(url + key);   // do not keep a failed (e.g. rate-limited) request for the whole visit
        return [];
      }));
  }
  return cache.get(url + key);
}

class ObCartUpsell extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.querySelector('.ob-upsell__sizes:not([hidden])')) {
        event.stopPropagation();   // close the size panel, not the drawer
        this.closePanels(true);
      }
    });
    this.load();
  }

  async load() {
    const track = this.querySelector('[data-rail-track]');
    const { url, exclude = '' } = this.dataset;
    const min = parseInt(this.dataset.min, 10) || 3;
    const limit = parseInt(this.dataset.limit, 10) || 8;
    const skip = new Set(exclude.split(',').filter(Boolean));
    const server = [...track.querySelectorAll('.ob-upsell__card')];
    const key = `|${exclude}`;   // cache per cart content (the server render also skips in-cart products)
    let complementary = [];
    let related = [];
    if (url) {
      complementary = await fetchCards(`${url}&intent=complementary`, key);
      const usable = complementary.filter((card) => !skip.has(card.dataset.productId));
      if (usable.length < min) related = await fetchCards(`${url}&intent=related`, key);
    }
    if (!this.isConnected) return;
    const bySource = (source) => server.filter((card) => card.dataset.source === source);
    const seen = new Set(skip);
    const titles = new Set(JSON.parse(this.dataset.titles || '[]').map((title) => title.toLowerCase()));
    const title = (card) => (card.querySelector('.ob-upsell__name')?.textContent || '').trim().toLowerCase();
    const cards = [...bySource('manual'), ...complementary, ...bySource('collection'), ...related]
      .filter((card) => !seen.has(card.dataset.productId) && seen.add(card.dataset.productId))
      .filter((card) => !titles.has(title(card)) && titles.add(title(card)))
      .slice(0, limit)
      .map((card) => (card.ownerDocument === document ? card : document.importNode(card, true)));
    track.replaceChildren(...cards);
    track.scrollLeft = 0;
    this.hidden = cards.length === 0;
    this.setAttribute('data-loaded', '');
    this.querySelector('ob-scroll-rail')?.update?.();
  }

  onClick(event) {
    const toggle = event.target.closest('[data-upsell-toggle][aria-controls]');
    if (!toggle) return;
    const panel = this.querySelector(`#${CSS.escape(toggle.getAttribute('aria-controls'))}`);
    if (!panel) return;
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    this.closePanels(false);
    if (!open) return;
    panel.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    panel.querySelector('button:not([disabled])')?.focus({ preventScroll: true });
  }

  closePanels(restoreFocus) {
    this.querySelectorAll('[data-upsell-toggle][aria-expanded="true"]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
      const panel = this.querySelector(`#${CSS.escape(toggle.getAttribute('aria-controls'))}`);
      if (panel) panel.hidden = true;
      if (restoreFocus) toggle.focus();
    });
  }
}

if (!customElements.get('ob-cart-upsell')) customElements.define('ob-cart-upsell', ObCartUpsell);
